// Original company-analysis lessons with hypothetical figures, not stock recommendations.
export const analysisLessons = [
  {
    id: 'revenue-margins', moduleId: 'analysis',
    title: '賣得更多，為什麼不一定賺得更多？', subtitle: '營收、毛利與淨利率', minutes: 7, tag: '拆開獲利',
    objectives: ['沿著損益表分辨不同層次的獲利', '同時比較金額、比率與變動原因'],
    paragraphs: [
      '營收是銷售商品或服務所認列的收入，還沒有扣除相關成本。毛利是營收減去營業成本；再扣除營業費用，可得到本課簡化情境中的營業利益。繼續計入營業外收支與所得稅，才走到稅後淨利。',
      '毛利率、營業利益率與淨利率，分別把各層獲利除以同期間營收。例如營業利益率 12%，表示每 100 元營收對應 12 元營業利益，不代表每賣一件產品就收到 12 元現金，也不是股東的投資報酬率。',
      '比率提高可能來自售價、產品組合、成本或會計分類改變，不能直接認定品牌變強。先比較公司自己的多期資料，再找業務相近的同業；金融業等特殊行業的收入與成本結構不同，不宜直接套用製造業的毛利率判讀。',
    ],
    example: {
      title: '假想製造商：1,000 萬元營收最後留下多少？',
      steps: ['同一年營收 1,000 萬、營業成本 650 萬，毛利 350 萬，毛利率 35%。', '營業費用 200 萬，營業利益為 150 萬，營業利益率 15%。', '假設另有營業外淨支出 50 萬與所得稅 20 萬，稅後淨利為 80 萬，淨利率 8%。'],
      conclusion: '三個比率回答不同問題；只看 35% 毛利率，會漏掉後續費用與損益。',
    },
    pitfalls: ['把營收增加直接當成獲利增加，沒有檢查成本與費用。', '把處分資產的一次性收益當成未來每年都能重複的本業成果。'],
    glossary: [{ term: '營業費用', definition: '例如銷售、管理與研發等費用，實際分類依公司業務與會計政策。' }, { term: '淨利率', definition: '同期間稅後淨利除以營收，通常以百分比表示。' }],
    takeaway: '由營收一路讀到淨利，才能知道錢在哪一層被留下或花掉。',
    exercise: '找一份同業公司的年度損益表，抄下營收、毛利、營業利益與稅後淨利，計算三個利潤率並核對期間。',
    quiz: { question: '營收 800 萬、營業成本 520 萬、營業費用 160 萬，假設無其他營業項目，營業利益率是多少？', options: ['35%', '15%', '20%'], answer: 1, explanation: '營業利益 = 800 − 520 − 160 = 120 萬；120 ÷ 800 = 15%。35% 則是毛利率。' },
    sources: [
      { label: '市場先生｜毛利率的主題閱讀', url: 'https://rich01.com/1-profit-margin/', kind: 'reading' },
      { label: 'SEC｜損益表與財務比率入門', url: 'https://www.sec.gov/investor/pubs/begfinstmtguide.htm', kind: 'official' },
    ],
  },
  {
    id: 'roe-leverage', moduleId: 'analysis',
    title: 'ROE 很高，是效率好還是分母變小？', subtitle: '平均權益與財務槓桿', minutes: 7, tag: '理解比率',
    objectives: ['用平均權益計算一個期間的 ROE', '檢查高 ROE 是否來自槓桿或權益縮小'],
    paragraphs: [
      'ROE 是權益報酬率，用同期間稅後淨利除以平均權益。本課的簡化公司只有普通股、沒有非控制權益，平均權益用期初與期末的平均值；若期間內有重大增資或減資，更細的時間平均可能較合適。',
      'ROE 同時受獲利與權益影響。淨利不變時，權益越小，比率越高；舉債、回購、配發資本或累積虧損都可能改變分母。借款還有利息與還本壓力，因此不能把高 ROE 全部解讀成經營能力提升。',
      '若權益接近零或為負，比率可能異常大，甚至出現虧損除以負權益而得到正數，這時比較高低失去意義。ROE 也不是你買股票的報酬，因為你支付的是市場價格，且未必收到全部公司盈餘。',
    ],
    example: {
      title: '假設：先看分子，再看分母',
      steps: ['公司一年稅後淨利 24 萬元，期初權益 180 萬、期末 220 萬，平均為 200 萬。', 'ROE = 24 ÷ 200 = 12%。', '另一個純計算情境若淨利仍為 24 萬、平均權益只剩 100 萬，ROE 就變為 24%；需要另外查明權益變小的原因。'],
      conclusion: '比率翻倍不代表公司多賺一倍，也不能直接推論股價會上漲。',
    },
    pitfalls: ['使用年底權益卻拿去與採平均權益的資料直接比較。', '忽略負權益與一次性損益，單靠 ROE 排名選公司。'],
    glossary: [{ term: '平均權益', definition: '用來配合一段期間獲利的權益基礎；簡化時可取期初與期末平均。' }, { term: '財務槓桿', definition: '使用負債等資金來源擴大資產運用，也會增加固定付款責任與股東風險。' }],
    takeaway: '每次看到高 ROE，都要一起問：淨利變了，還是權益變了？',
    exercise: '找公司連續兩年的權益與當年淨利，先算簡化 ROE，再閱讀借款、配息與增減資資訊，列出可能影響分母的事件。',
    quiz: { question: '期初權益 300 萬、期末 500 萬，同年淨利 40 萬，依本課平均權益算法，ROE 為何？', options: ['8%', '13.33%', '10%'], answer: 2, explanation: '平均權益 =（300 + 500）÷ 2 = 400 萬；40 ÷ 400 = 10%。不同分母會造成不同結果。' },
    sources: [
      { label: '市場先生｜ROE 與判讀限制', url: 'https://rich01.com/what-is-roe/', kind: 'reading' },
      { label: '證交所財務比較 E 點通｜權益報酬率公式', url: 'https://mopsfin.twse.com.tw/index', kind: 'official' },
    ],
  },
  {
    id: 'book-value', moduleId: 'analysis',
    title: '股價低於淨值，就一定便宜嗎？', subtitle: '每股淨值與 P/B 的限制', minutes: 7, tag: '看懂帳面',
    objectives: ['用普通股權益與股數計算簡化每股淨值', '區分帳面價值、變現金額與市場評價'],
    paragraphs: [
      '公司帳面權益來自資產減去負債。本課假設只有普通股、無非控制權益，每股淨值 = 普通股權益 ÷ 在外普通股股數；P/B = 股價 ÷ 每股淨值。實際公司有不同權益結構時，應先讀資料網站的定義。',
      '帳面價值遵循會計認列與衡量規則，不是今天把整間公司出售能收到的保證金額。存貨可能過時、應收帳款可能收不回，資產減損也可能降低權益；公司自行建立的品牌等價值，則未必全部列成帳上資產。',
      '所以 P/B 低於 1，可能反映市場擔心資產品質或未來虧損，並非自動出現便宜買點。資產輕重、無形資產與會計政策會影響比較；淨值接近零或為負時，比率也難以正常解讀。',
    ],
    example: {
      title: '假設：帳面數字也會改變',
      steps: ['普通股權益 240 萬元、在外股數 12 萬股，每股淨值為 20 元。', '股價 16 元時，P/B = 16 ÷ 20 = 0.8 倍。', '若後來確認的減損讓普通股權益降為 180 萬，股數不變，每股淨值變 15 元；同樣股價 16 元的 P/B 約為 1.07 倍。'],
      conclusion: '價格沒有變，分母更新就能改變比率；低於一倍不是價值保證。',
    },
    pitfalls: ['以為公司清算時一定能按帳面淨值分回現金。', '直接拿重資產製造商與輕資產服務公司的 P/B 排高低。'],
    glossary: [{ term: '減損', definition: '資產帳面金額需要調降以反映可回收價值下降的會計處理。' }, { term: 'P/B', definition: '股價相對每股帳面淨值的倍數，也稱股價淨值比。' }],
    takeaway: 'P/B 是提問的起點：帳上的資產有什麼，價值是否可靠？',
    exercise: '找一家公司資產負債表，圈出存貨、應收款與無形資產，再查看附註是否有減損或重要估計。',
    quiz: { question: '在簡化普通股情境中，權益 360 萬元、股數 18 萬股、股價 24 元，P/B 為何？', options: ['1.2 倍', '0.83 倍', '20 倍'], answer: 0, explanation: '每股淨值 = 360 ÷ 18 = 20 元；P/B = 24 ÷ 20 = 1.2 倍。這個數字本身不判定值得買進。' },
    sources: [
      { label: '市場先生｜股價淨值比', url: 'https://rich01.com/what-is-pb-ratio/', kind: 'reading' },
      { label: '證交所｜股價淨值比的資料定義', url: 'https://accessibility.twse.com.tw/zh/trading/historical/bwibbu.html', kind: 'official' },
      { label: 'IFRS Foundation｜無形資產認列', url: 'https://www.ifrs.org/issued-standards/list-of-standards/ias-38-intangible-assets/', kind: 'official' },
    ],
  },
  {
    id: 'free-cash-flow', moduleId: 'analysis',
    title: '賺到的現金，扣掉設備支出還剩多少？', subtitle: '營業現金流與 FCF', minutes: 7, tag: '追蹤現金',
    objectives: ['依明確定義計算自由現金流', '避免把投資現金流或現金餘額誤認為 FCF'],
    paragraphs: [
      '營業活動現金流量淨額（CFO）記錄營運產生或消耗的現金，與損益表淨利不同。若銷貨尚未收款或存貨增加，帳面賺錢也可能沒有帶來同等現金。要配合多期資料與營運資金變化閱讀。',
      '本課採常見簡化定義：自由現金流 FCF = CFO − 資本支出的現金支付額，且將支付額寫為正數。FCF 不是具有統一定義的標準會計報表項目；公司可能另作調整，必須查看公式及與報表數字的調節。',
      '投資活動現金流還可能包含買賣金融資產、併購或出售設備，不能全部當成資本支出。FCF 也未必已扣掉到期債務本金等必要付款，不能直接解讀為全部可自由配給股東的錢；負值要追查原因，不等於公司立即失敗。',
    ],
    example: {
      title: '假設：賣掉舊設備，會讓哪個數字增加？',
      steps: ['本期 CFO 為 90 萬元，購買新設備付現 50 萬，依本課定義 FCF = 90 − 50 = 40 萬。', '另出售舊設備收到 20 萬，若沒有其他投資活動，投資現金流淨額為 −30 萬。', '直接把 CFO 加投資現金流會得到 60 萬，和本課 FCF 40 萬不是同一指標。', '若還有債務本金 35 萬到期，這項付款也不能因 FCF 為正就忽略。'],
      conclusion: '先說明扣掉哪些支出，再談剩餘現金的意義。',
    },
    pitfalls: ['用營業現金流加全部投資現金流，卻標成相同定義的 FCF。', '把正 FCF 當成股利必定增加，漏看債務與後續投資需要。'],
    glossary: [{ term: '資本支出', definition: '本課指購建設備等長期資產的現金支付；實際公司揭露範圍需確認。' }, { term: '非 GAAP 指標', definition: '不直接依一般公認會計原則定義的補充指標，必須查看計算與調節方式。' }],
    takeaway: 'FCF 的名字不夠，公式與尚未扣除的付款責任更重要。',
    exercise: '找一份現金流量表，分開記錄 CFO、購建設備付現與投資現金流總額，解釋後兩者為什麼可能不同。',
    quiz: { question: 'CFO 120 萬元，購建設備付現 75 萬，另出售投資收到 20 萬，依本課定義 FCF 為何？', options: ['65 萬元', '45 萬元', '120 萬元'], answer: 1, explanation: '本課定義為 CFO 減資本支出：120 − 75 = 45 萬。出售投資收到的 20 萬不是這個公式中的 CFO。' },
    sources: [
      { label: '市場先生｜自由現金流的主題閱讀', url: 'https://rich01.com/what-free-cash-flow/', kind: 'reading' },
      { label: 'SEC｜非 GAAP 指標與自由現金流（102.07）', url: 'https://www.sec.gov/rules-regulations/staff-guidance/corporation-finance-interpretations/non-gaap-financial-measures', kind: 'official' },
    ],
  },
  {
    id: 'financial-debt', moduleId: 'analysis',
    title: '負債不只一種，還款能力怎麼看？', subtitle: '負債結構與利息保障倍數', minutes: 7, tag: '檢查償債',
    objectives: ['區分總負債與有息借款', '計算利息保障倍數並知道它漏掉什麼'],
    paragraphs: [
      '總負債可能包含銀行借款、公司債、應付帳款與預收款等項目，不是每一筆都收利息。本課的負債比定義為總負債 ÷ 總資產；若看到 debt ratio 或 debt-to-equity 等字樣，應先確認分子採總負債還是有息債務。',
      '利息保障倍數可用 EBIT ÷ 同期間利息費用。這裡 EBIT 指未扣所得稅與利息的利益，可由稅前淨利加回利息費用理解；不要不加區分地直接拿稅後淨利或營業利益代替。',
      '這個倍數比較的是會計利益與利息，不是帳戶現金能付幾次，也沒有直接測試本金到期壓力。仍要讀到期日、現金流與借款條件；若利息為零就不能直接除，EBIT 為負時也不能當成正常的正倍數比較。金融業另有特殊結構。',
    ],
    example: {
      title: '假設：同一家公司，要問兩組問題',
      steps: ['資產 500 萬、總負債 300 萬，負債比為 60%；其中有息借款僅 180 萬，不等於總負債。', '同年度 EBIT 為 48 萬、利息費用 12 萬，利息保障倍數為 4 倍。', '即使是 4 倍，仍需查下月是否有大額本金到期，以及公司能否取得足夠現金。'],
      conclusion: '負債金額、利息負擔與到期付款是不同面向，沒有一個比率能全部回答。',
    },
    pitfalls: ['把所有負債都當成銀行貸款，忽略無息營運負債。', '認為利息保障倍數大於某個門檻就保證不會違約。'],
    glossary: [{ term: '有息債務', definition: '通常需要支付利息的借款或債券等債務；比較時須明確說明納入範圍。' }, { term: 'EBIT', definition: '息前稅前利益，即尚未扣除利息與所得稅的利益。' }],
    takeaway: '公司有沒有能力還錢，要同時看賺多少、收到多少與何時要付。',
    exercise: '在一份非金融業財報的借款附註找出一年內到期金額，並和利息費用、營業現金流放在一起閱讀。',
    quiz: { question: '同年度稅前淨利 36 萬、利息費用 9 萬，依本課定義利息保障倍數為何？', options: ['4 倍', '3 倍', '5 倍'], answer: 2, explanation: 'EBIT = 36 + 9 = 45 萬；45 ÷ 9 = 5 倍。直接用稅前淨利除以利息，漏了加回利息這一步。' },
    sources: [
      { label: '市場先生｜利息保障倍數', url: 'https://rich01.com/what-times-interest-earned/', kind: 'reading' },
      { label: '證交所財務比較 E 點通｜負債比與利息保障倍數', url: 'https://mopsfin.twse.com.tw/index', kind: 'official' },
    ],
  },
  {
    id: 'valuation-growth', moduleId: 'analysis',
    title: '成長很快，可以接受更貴的價格嗎？', subtitle: '本益成長比 PEG 與假設敏感度', minutes: 7, tag: '估值思考',
    objectives: ['使用正確的成長率單位計算 PEG', '檢查估值對未來成長假設有多敏感'],
    paragraphs: [
      'PEG 把本益比再除以年度 EPS 成長率，試著把價格與成長放在一起看。慣用算法將百分比寫成數字：12% 成長在分母用 12，而不是 0.12；混用單位會把結果放大 100 倍。',
      '資料可能使用歷史成長或分析師預測，也可能用一年或多年的年化成長。比較前要標明 EPS 期間、成長期間與資料日期，選擇一致口徑。預估成長不是公司承諾，低基期或一次性盈餘也可能讓成長率看起來異常高。',
      'PEG 沒有通用的安全買點。它沒有完整描述負債、再投資需求與成長能維持多久；成長接近零、為負，或 EPS 本身為負時，也不適合機械式比較。與其只找最低數字，更應測試假設改變後結果會如何。',
    ],
    example: {
      title: '假設：只改成長預估，便宜的印象就改變',
      steps: ['某假想公司採用一致預估口徑的本益比為 24 倍，預期年度 EPS 成長率 12%。', 'PEG = 24 ÷ 12 = 2。', '若本益比暫時不變，但預估成長率修正為 6%，PEG 就變成 4；這是敏感度練習，沒有預測實際股價。'],
      conclusion: '估值指標的精確小數，不能讓成長假設變得更可靠。',
    },
    pitfalls: ['把 15% 的成長率用 0.15 代入慣用 PEG 公式。', '把 PEG 低於 1 當成必買規則，沒有檢查成長是否可持續。'],
    glossary: [{ term: 'PEG', definition: '本益比除以以百分比數字表示的年度 EPS 成長率，需明確標示採用期間與假設。' }, { term: '敏感度分析', definition: '改變一項假設，觀察估算結果受影響的程度。' }],
    takeaway: '成長預估越不確定，越要追問估值用了什麼假設。',
    exercise: '固定假想本益比 18 倍，分別代入 EPS 成長 6%、9%、12%，算出 PEG，再寫下無法由這個比率回答的一項問題。',
    quiz: { question: '依慣用算法，本益比 30 倍、預估年度 EPS 成長 15%，PEG 是多少？', options: ['2', '200', '0.5'], answer: 0, explanation: '30 ÷ 15 = 2。15% 在這個比率的慣用公式中以 15 代入；結果不構成買賣建議。' },
    sources: [
      { label: '市場先生｜本益成長比 PEG', url: 'https://rich01.com/ratio-peg/', kind: 'reading' },
      { label: '嘉信理財｜PEG 算法、口徑與限制', url: 'https://www.schwab.com/learn/story/what-is-peg-ratio-basics-formula-and-risks', kind: 'official' },
    ],
  },
  {
    id: 'cyclical-earnings', moduleId: 'analysis',
    title: '本益比很低，也可能是獲利正好在高峰', subtitle: '景氣循環與盈餘基期', minutes: 7, tag: '觀察週期',
    objectives: ['說明景氣變動如何影響 EPS 與本益比', '避免用單一年份推估長期獲利'],
    paragraphs: [
      '某些公司的需求、售價或原料成本對經濟與產業供需很敏感，盈餘會隨週期大幅變動。不能只靠產業名稱就判定每家公司完全相同；客戶結構、成本與產能，也會改變受影響的程度。',
      '本益比的分母是 EPS。若當期獲利恰好很高，本益比可能很低；之後 EPS 回落，即使股價不變，本益比也會升高。相反地，低谷 EPS 接近零時，本益比可能異常大，虧損時則不宜正常解讀。',
      '較完整的研究會查看多期營收、利潤率、產能、存貨與現金流，嘗試理解供需與成本，而非把「高本益比買、低本益比賣」當公式。週期轉折通常只有事後才清楚，多年平均盈餘也只是估計，可能因產業結構改變而失效。',
    ],
    example: {
      title: '假設：股價沒變，EPS 先高後低',
      steps: ['股價暫設 100 元，繁忙年度 EPS 20 元，本益比為 5 倍。', '下一年 EPS 若降到 4 元，同樣股價的本益比變為 25 倍。', '真實價格也會改變；這裡固定股價，是為了看清分母如何影響比率。'],
      conclusion: '看到 5 倍，還要問 20 元 EPS 能維持多久，不能直接宣稱五年就會回本。',
    },
    pitfalls: ['用獲利最高的一年當成永遠不變的正常年度。', '看到虧損或極高本益比，就認定一定接近景氣底部。'],
    glossary: [{ term: '景氣循環', definition: '經濟或產業供需隨時間擴張與收縮，並非固定長度的時鐘。' }, { term: '盈餘基期', definition: '比較成長或估值所使用的起始盈餘，異常高低會影響判讀。' }],
    takeaway: '便宜或昂貴的判斷，不能只建立在一個剛好特別好的年度。',
    exercise: '用假想股價 72 元，搭配 EPS 12、6、3 元算本益比；再列出你想確認的兩項產業資料。',
    quiz: { question: '股價固定 72 元，EPS 從 12 元降到 3 元，本益比會如何變化？', options: ['從 24 倍降到 6 倍', '從 6 倍升到 24 倍', '一直維持 6 倍'], answer: 1, explanation: '72 ÷ 12 = 6；72 ÷ 3 = 24。分母變小會放大本益比，並不需要股價先上漲。' },
    sources: [
      { label: '市場先生｜景氣循環股的特性', url: 'https://rich01.com/what-is-cyclical-stocks/', kind: 'reading' },
      { label: '嘉信理財｜本益比與景氣循環的限制', url: 'https://www.schwab.com/learn/story/stock-analysis-using-pe-ratio', kind: 'official' },
    ],
  },
  {
    id: 'company-checklist', moduleId: 'analysis',
    title: '把零散指標，組成一張公司研究筆記', subtitle: '商業模式、財報與待確認問題', minutes: 8, tag: '綜合練習',
    objectives: ['把數字變化連回公司的實際營運', '分開記錄事實、解釋與仍待查證的假設'],
    paragraphs: [
      '研究一家公司，可以先用一句話描述它賣什麼、誰付錢、主要成本在哪裡，再找營收與獲利資料驗證。數字不是單獨的答案；銷售成長可能來自價格、數量或併購，不同原因對未來的意義不同。',
      '一張筆記可依序記下營運、獲利、資產負債、現金流與估值，旁邊放來源、日期、單位與待確認問題。公司年報的風險與管理階層說明、財報附註和查核報告，都能補充表面比率沒有說清楚的內容。',
      '先把「公司公布的事實」「你對原因的推論」「尚未找到證據」分開。主管機關公開收錄文件不等於替內容或投資價值背書；研究筆記的目的，是知道理解到哪裡，而不是用固定分數自動判定該買哪一檔股票。',
    ],
    example: {
      title: '假想案例：成長與現金變化，指向不同問題',
      steps: ['前一年營收 1,200 萬、淨利 120 萬、CFO 150 萬；今年分別為 1,440 萬、160 萬、70 萬。', '營收增加 20%，淨利增加約 33.33%，但 CFO 減少 80 萬。', '待確認：應收款或存貨是否增加？付款時點是否不同？是否有一次性項目？', '回查現金流量表調整項目與附註，再記錄有證據支持的原因，不直接推定造假或下買賣結論。'],
      conclusion: '一個不一致的訊號，應帶出下一個查證問題，而不是立刻貼上好公司或壞公司的標籤。',
    },
    pitfalls: ['把每個比率加分後直接產生買進結論，忽略定義與產業差異。', '只有結論沒有日期與原始連結，之後無法核對或更新。'],
    glossary: [{ term: '財報附註', definition: '補充會計政策、項目明細、重大估計與其他報表背景的說明。' }, { term: '管理階層說明', definition: '公司管理者對營運、財務變化與風險的解釋，仍需與數字和其他證據核對。' }],
    takeaway: '完成研究，不是消除所有未知，而是清楚知道哪些判斷已有證據。',
    exercise: '選一家公司只做閱讀練習，寫下商業模式、三項數字、兩項風險與一個待確認問題，每項都附資料日期與來源。',
    quiz: { question: '公司淨利增加、營業現金流卻下降，最合理的下一步是什麼？', options: ['直接判定一定造假', '只看淨利成長就買進', '查應收款、存貨、付款時點與現金流附註'], answer: 2, explanation: '差異可能有多種原因，需要閱讀原始資料。單一訊號既不能證明造假，也不足以形成投資結論。' },
    sources: [
      { label: '市場先生｜財報分析入門方向', url: 'https://rich01.com/how-learn-financial-report/', kind: 'reading' },
      { label: 'SEC｜年報的章節與查核範圍', url: 'https://www.sec.gov/answers/reada10k.htm', kind: 'official' },
    ],
  },
];
